import React from 'react';

import {
  ButtonBase, Dialog, DialogProps,
  Theme,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

interface StyleProps {
  backgroundColor?: string;
  scroll: DialogProps['scroll'];
  fullWidth: boolean;
  titleSize: 'default' | 'large';
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  header: (props) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    flexShrink: 0,
    padding: props.titleSize === 'large' ? '8px 20px 4px' : undefined,
  }),
  headerCopy: { minWidth: 0 },
  dialog: (props) => ({
    '& > div > div': {
      width: props.fullWidth ? undefined : 'fit-content',
      padding: '16px 16px 22px',
      backgroundColor: props.backgroundColor || theme.palette.secondary.main,
    },
  }),
  title: (props) => ({
    fontWeight: 'bold',
    fontSize: props.titleSize === 'large' ? '22px' : '14px',
    lineHeight: props.titleSize === 'large' ? '30px' : '20px',
    color: theme.palette.white.main,
    marginRight: '50px',

    '& > svg': {
      marginRight: '18px',
      verticalAlign: 'middle',
    },
  }),
  subtitle: {
    fontSize: '14px',
    lineHeight: '22px',
    fontWeight: 400,
    marginTop: '6px',
    color: theme.palette.white.main,
  },
  message: (props) => ({
    padding: '0 20px 0',
    color: theme.palette.white.main,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    margin: '16px 0 20px',
    overflowWrap: 'break-word',
    minHeight: 0,
    overflowY: props.scroll === 'paper' ? 'auto' : undefined,
  }),

  buttons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    flexShrink: 0,

    '& > button': {
      margin: '0 10px 0',
      backgroundColor: theme.palette.primary.light,
      borderRadius: '8px',
      padding: '8px 12px',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      color: theme.palette.grayScale9.main,
      textTransform: 'uppercase',
    },
  },
}));

interface Props {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  titleSize?: 'default' | 'large';
  message: React.ReactNode | string;
  onClose: () => void;
  buttons: React.ReactNode[];
  backgroundColor?: string;
  textColor?: string;
  maxWidth?: DialogProps['maxWidth'];
  scroll?: DialogProps['scroll'];
  fullWidth?: boolean;
  disableClose?: boolean;
}

const ActionModal = ({
  icon, title, subtitle, titleSize = 'default', message, onClose, buttons = [], backgroundColor, textColor, maxWidth = 'xs', scroll = 'body', fullWidth = false, disableClose = false,
}: Props) => {
  const classes = useStyles({
    backgroundColor, scroll, fullWidth, titleSize,
  });
  const theme = useTheme();

  return (
    <Dialog
      open
      scroll={scroll}
      fullWidth={fullWidth}
      keepMounted
      maxWidth={maxWidth}
      onClose={disableClose ? undefined : onClose}
      disableEscapeKeyDown={disableClose}
      PaperProps={{ 'aria-label': title, style: scroll === 'paper' ? { overflowY: 'hidden' } : undefined }}
      className={classes.dialog}
    >
      <div className={classes.header}>
        <div className={classes.headerCopy}>
          <div className={classes.title} style={{ color: textColor }} role="heading" aria-level={2}>
            {icon}
            {title}
          </div>
          {subtitle && <div className={classes.subtitle} style={{ color: textColor }}>{subtitle}</div>}
        </div>
        {/* TODO: modalCloseButton??? */}
        {/* <ButtonBase className={classes.modalCloseButton} aria-label="Close" onClick={onClose}> */}
        <ButtonBase aria-label="Close" onClick={onClose} disabled={disableClose}>
          <CloseIcon htmlColor={textColor || theme.palette.white.main} />
        </ButtonBase>
      </div>
      <div className={classes.message} style={{ color: textColor }}>{message}</div>
      <div className={classes.buttons}>
        {buttons.map((button, i) => <React.Fragment key={i}>{button}</React.Fragment>)}
      </div>
    </Dialog>
  );
};

export default ActionModal;
